/**
 * B1 — map a deserialized B0 ReportCriteria into the concrete Reports-page state
 * atoms. Pure + framework-free so it can be unit-tested; Reports.tsx applies the
 * result through its setters + the FilterContext setters.
 *
 * Compare slots are intentionally NOT hydrated here: no subscription can carry
 * them until the B5 subscribe UI can create a compare subscription, so there is
 * nothing to reproduce yet. Compare deep-linking lands with B5.
 */
import type { ReportCriteria } from './reportCriteria';
import type { DatePreset, TimeGranularity, ReportViewMode } from '@/types/reports';
import type { IndustryType } from '@/types';
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';

export interface ReportsHydration {
  /** Reports-local `filters` (dates + selections). */
  filters: {
    datePreset: DatePreset;
    customDateRange: { start: Date | null; end: Date | null };
    companyIds: string[];
    groupIds: string[];
  };
  /** Reports-local `analyticsFilters` subset the preset controls. */
  analytics: {
    groupIds: string[];
    modelCodes: string[];
    granularity: TimeGranularity;
    viewMode: ReportViewMode;
    /**
     * Always false on a link: a "Use production dates" subscription stored its
     * dates (filters.customDateRange), and its emails cover those. Turning the
     * live lookup back on would show the productions' dates as they are today.
     */
    useFullGroup: boolean;
  };
  /** Shared header FilterContext values. */
  companyIds: string[];
  industryTypes: IndustryType[];
  models: string[];
  includeAllDeployments: boolean;
}

// Closed IndustryType set — a stored criteria could carry a value the current
// build no longer supports; filter to known values before it reaches
// setSelectedIndustryTypes (which feeds company/data filtering).
const KNOWN_INDUSTRY_TYPES: readonly IndustryType[] = ['film', 'event', 'gov', 'utility', 'relief'];

/**
 * Stored date → Date, or null when absent/unparseable (a bad range degrades to
 * "no custom range"). A plain YYYY-MM-DD is that LOCAL calendar day: `new Date`
 * would read it as UTC midnight, which shows as the day before in the Americas.
 */
function parseIso(value: string | null): Date | null {
  if (!value) return null;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// 'all' opens as every option selected: the filter menus' default. An empty
// selection would be the "nothing selected" state, which shows no report and
// (being shared filters) would clear the user's Dashboard and Map filters too.
const ALL_MODEL_CODES = Object.keys(MODEL_LABELS);

/**
 * Project a validated ReportCriteria onto the Reports-page state. Caller must pass
 * a criteria that already survived deserializeReportCriteria (schema + closed-set
 * guards); this function only reshapes, it does not re-validate.
 */
export function criteriaToReportsHydration(
  criteria: ReportCriteria,
  groups: readonly { id: string; company_id: string | null }[] | null = [],
): ReportsHydration {
  // "All units in <company>" opens as every one of its productions and sites
  // plus its Unassigned bucket selected, which is that report on screen. When
  // the company's groups are unknown (`null`, the read failed) it opens with
  // nothing selected, the "choose a production" view, never a partial report.
  const onlyCompany = criteria.companyIds.length === 1 ? criteria.companyIds[0] : null;
  const groupIds = criteria.groupScope === 'all' && onlyCompany
    ? (groups === null
      ? []
      : [...groups.filter((g) => g.company_id === onlyCompany).map((g) => g.id), `__unassigned_${onlyCompany}__`])
    : [...criteria.groupIds];
  const models = criteria.modelScope === 'all' ? [...ALL_MODEL_CODES] : [...criteria.modelCodes];
  const industryTypes: IndustryType[] = criteria.industryScope === 'all'
    ? [...INDUSTRY_TYPE_ORDER]
    : criteria.industryTypes.filter(
      (t): t is IndustryType => (KNOWN_INDUSTRY_TYPES as readonly string[]).includes(t),
    );
  return {
    filters: {
      datePreset: criteria.datePreset,
      customDateRange: {
        start: parseIso(criteria.customDateRange?.start ?? null),
        end: parseIso(criteria.customDateRange?.end ?? null),
      },
      companyIds: [...criteria.companyIds],
      groupIds: [...groupIds],
    },
    analytics: {
      groupIds: [...groupIds],
      modelCodes: [...models],
      granularity: criteria.granularity,
      viewMode: criteria.reportType,
      useFullGroup: false,
    },
    companyIds: [...criteria.companyIds],
    industryTypes,
    models,
    includeAllDeployments: criteria.includeAllDeployments,
  };
}
